import SeeMore from 'ui/modules/Pagination/SeeMore';
import React, { RefObject, useState } from 'react';
import { PaginatedResult } from 'types';
import { PaginationOptions } from 'types/api';
import { Comment, CommentId } from 'types/content';
import { addItem, addPage, updateInList } from 'util/paginationUtils';
import usePaginatedResourceLegacy from 'util/resource/usePaginatedResourceLegacy';
import LoadableResource from 'util/resource/Resource';
import Card from 'ui/views/cards/Card';
import Button from 'ui/elements/buttons/Button';
import Loading from 'ui/elements/Loading';
import CommentField, { UpdateContext } from './CommentField';
import CommentContainer from './CommentContainer';
import { PostConversationMessage } from 'apis/CompanyAPI/conversations/conversationTypes';
import { UpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import ReactQuill from 'react-quill';
import {
  communityMentionSearchFunction,
  companyUserMentionSearchFunction,
  roomMentionSearchFunction,
} from 'ui/elements/form/RichTextEditor/utils';
import { invalidate } from 'hooks/useSWR';
import { communityActivityKey } from 'pages/Community/Overview/CommunityUpdates/useCommunityActivity';
import { useNotifyContext } from 'contexts/NotificationContext';

interface Props {
  getComments: UpdateAPI['comments']['list'];
  postComment: UpdateAPI['comments']['post'];
  replyToComment: UpdateAPI['comments']['replyToComment'];
  getReplies: UpdateAPI['comments']['getReplies'];
  deleteComment: UpdateAPI['comments']['delete'];
  updateId: string;
  autoFocus: boolean;
  editorRef?: RefObject<ReactQuill>;
  onInputClicked: () => void;
  commentsCount: number;
  context: UpdateContext;
}

export type OnCommentDeleted = (commentId: CommentId, responseTo?: string) => Promise<any>;

const markAsDeleted = (comment: Comment) => ({
  ...comment,
  deleted: true,
  content: 'Comment has been removed',
});

const mapReplies = (comment: Comment) => ({ replyTo: comment.id, replies: { ...comment.replies, page: 0 } });
const compareComment = (commentId: string) => (c: Comment) => c.id === commentId;

const PAGE_SIZE = 10;

function CommentsContainer(props: Props) {
  const notify = useNotifyContext();
  const [replies, setReplies] = useState<{ replyTo: string; replies: PaginatedResult<Comment> }[]>([]);

  const [commentsResource, setCommentsResource, , seeMore] = usePaginatedResourceLegacy(
    async paginationOptions => {
      const r = await props.getComments(props.updateId, paginationOptions || { limit: PAGE_SIZE });
      setReplies([...replies, ...r.values.map(mapReplies)]);

      return r;
    },
    [props.updateId],
  );

  const onSeeMoreReplies = async (commentId: string, paginationOptions: PaginationOptions) => {
    const replies = await props.getReplies(props.updateId, commentId, paginationOptions);

    setReplies(prevState =>
      prevState.map(reply => {
        if (reply.replyTo === commentId) {
          return { replyTo: commentId, replies: addPage(reply.replies, replies, (c: Comment) => c.id) };
        }
        return reply;
      }),
    );
  };

  const onReplyComment = async (commentId: string, comment: string) => {
    try {
      const response = await props.replyToComment(props.updateId, commentId, comment);
      const something = !replies.find(reply => reply.replyTo === commentId)
        ? [
            ...replies,
            {
              replyTo: commentId,
              replies: { page: 1, pageSize: 1, total: 1, limit: PAGE_SIZE, values: [response], pages: 1 },
            },
          ]
        : replies.map(reply =>
            reply.replyTo === commentId ? { ...reply, replies: addItem(reply.replies, response, 'after') } : reply,
          );
      setReplies(something);
      if (props.context.type === 'community') {
        invalidate(communityActivityKey(props.context.community.id));
      }
      notify('success', 'Your reply has been posted');
    } catch (e) {
      notify('error', 'Could not save comment. Please try again later.');
    }
  };

  const onPostComment = async (comment: PostConversationMessage) => {
    try {
      const response = await props.postComment(props.updateId, comment.content);
      if (commentsResource.state === 'fetched') {
        setCommentsResource(prev => addItem(prev, response));
        notify('success', 'Your comment has been posted');
      }
      if (props.context.type === 'community') {
        invalidate(communityActivityKey(props.context.community.id));
      }
    } catch (e) {
      notify('error', 'Could not save comment. Please try again later.');
    }
  };

  const onCommentDeleted = async (commentId: CommentId, responseTo?: string) => {
    try {
      await props.deleteComment(props.updateId, commentId);

      const updatedReplies = replies.map(reply =>
        reply.replyTo === responseTo
          ? { ...reply, replies: updateInList(reply.replies, compareComment(commentId), markAsDeleted) }
          : reply,
      );

      setCommentsResource(prev => (!responseTo ? updateInList(prev, compareComment(commentId), markAsDeleted) : prev));
      setReplies(updatedReplies);
      if (props.context.type === 'community') {
        invalidate(communityActivityKey(props.context.community.id));
      }
      notify('success', 'Comment has been removed');
    } catch (error) {
      notify('error', 'Sorry, we were not able to remove this comment at the moment. Please try again later.');
    }
  };

  const context = props.context;

  const handleSearchForMentions =
    context.type === 'room'
      ? (searchTerm: string) => roomMentionSearchFunction(searchTerm, context.roomId)
      : context.type === 'community'
        ? (searchTerm: string) => communityMentionSearchFunction(searchTerm, context.visibility, context.community.id)
        : context.type === 'company'
          ? (searchTerm: string) => companyUserMentionSearchFunction(searchTerm, context.visibility, context.companyId)
          : undefined;

  return (
    <Card padding="none">
      <CommentField
        id={props.updateId}
        onInputClicked={props.onInputClicked}
        onPost={onPostComment}
        handleSearchForMentions={handleSearchForMentions}
        editorRef={props.editorRef}
        autoFocus={props.autoFocus}
        editorPlaceholder="Write a comment"
        className="u-content-padding-x u-content-padding-top u-half-padding-bottom"
      />
      <LoadableResource
        resource={commentsResource}
        renderLoading={
          props.commentsCount > 0
            ? () => (
                <div className="u-flex-center u-section-spacing-top">
                  <Loading size={25} />
                </div>
              )
            : 'Nothing'
        }
      >
        {comments => (
          <div className="u-half-padding-bottom">
            {comments.values
              .filter(c => !c.responseTo)
              .map(comment => {
                const currentReplies = replies.find(reply => reply.replyTo === comment.id);
                return (
                  <CommentContainer
                    key={comment.id}
                    comment={comment}
                    onReply={(commentId: string, comment: string) => onReplyComment(commentId, comment)}
                    updateId={props.updateId}
                    context={props.context}
                    onDeleteComment={onCommentDeleted}
                    replies={currentReplies && currentReplies.replies}
                    onSeeMoreReplies={(paginationOptions: PaginationOptions) =>
                      onSeeMoreReplies(comment.id, paginationOptions)
                    }
                  />
                );
              })}
            <SeeMore
              render={fetch => (
                <Button onClick={fetch} kind="tertiary">
                  See more
                </Button>
              )}
              className="u-content-spacing-y"
              resource={comments}
              loadResource={seeMore}
              limit={PAGE_SIZE}
            />
          </div>
        )}
      </LoadableResource>
    </Card>
  );
}

export default CommentsContainer;
